/**
 * [DATE-STYLE 2026-09-29, user] NEW FILE — "use date style this: Sep 29, 2026", everywhere on screen.
 *
 *   a day            Sep 29, 2026   (no leading zero: Sep 8, 2026)
 *   a day + time     Sep 29, 2026, 4:28 PM
 *   a month          Sep 2026
 *
 * Most tabs already had their own Intl formatter; those were switched to these same options in place.
 * This file holds the few conversions that start from TEXT the server sends (so there is no Date to
 * format): each keeps the digits exactly as sent — no time-zone shift — and only changes how they read.
 * Downloaded Excel/PDF files keep their own formats on purpose.
 */

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM' (or 'YYYY-MM-DD…') → "Sep 2026". */
export function fmtMonthYear(ym: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(ym || '');
  return m ? `${MON[Number(m[2]) - 1]} ${m[1]}` : ym;
}

/** 'YYYY-MM-DD' (anything after the day is ignored) → "Sep 29, 2026"; other text comes back as is. */
export function fmtDayText(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  return m ? `${MON[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : value;
}

/** 'YYYY-MM-DD HH:MM[:SS]' → "Sep 29, 2026, 8:52 AM" (the digits as sent, no zone conversion). */
export function fmtDayTimeText(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value || '');
  if (!m) return fmtDayText(value);
  const h = Number(m[4]);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${MON[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}, ${h12}:${m[5]} ${h < 12 ? 'AM' : 'PM'}`;
}

/** A day the server sends without a year ("Oct 02") → "Oct 2, 2026". The year comes from `ref`
 *  (when the figure was produced); a month more than six months before it belongs to the next year
 *  (a December refresh naming a January payout). */
export function fmtShortDayWithYear(value: string, ref: Date): string {
  const m = /^([A-Za-z]{3})\s+(\d{1,2})$/.exec((value || '').trim());
  if (!m) return value;
  const mi = MON.findIndex((x) => x.toLowerCase() === m[1].toLowerCase());
  if (mi < 0) return value;
  const year = ref.getFullYear() + (mi < ref.getMonth() - 6 ? 1 : 0);
  return `${MON[mi]} ${Number(m[2])}, ${year}`;
}

/** EOM's 10-day windows as the server labels them ("01-10 Sep 2026") → "Sep 1–10, 2026".
 *  Any other label ("Overall Sep 2026") comes back unchanged. */
export function fmtDayRangeLabel(label: string): string {
  const m = /^(\d{1,2})-(\d{1,2}) ([A-Za-z]{3}) (\d{4})$/.exec((label || '').trim());
  return m ? `${m[3]} ${Number(m[1])}–${Number(m[2])}, ${m[4]}` : label;
}
